import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Edit3, Rocket, BarChart2, ExternalLink, Copy,
  Package, DollarSign, Users, Clock, Link2, FileCode, Image, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import theme from '../theme';
import RapPanel from '../components/RapPanel';
import ClaimLinksPanel from '../components/ClaimLinksPanel';
import UtilityRulesPanel from '../components/UtilityRulesPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADIENTS = [
  'linear-gradient(135deg, #6C5CE7, #00D9FF)',
  'linear-gradient(135deg, #FF7A59, #FFB84D)',
  'linear-gradient(135deg, #20E3B2, #6C5CE7)',
  'linear-gradient(135deg, #BC13FE, #FF10F0)',
  'linear-gradient(135deg, #00D9FF, #20E3B2)',
];

const STATUS_BADGE = {
  DRAFT: { bg: '#F3F4F6', text: '#6B7280', label: 'Draft' },
  ACTIVE: { bg: '#D1FAE5', text: '#059669', label: 'Active' },
  READY_TO_ACTIVATE: { bg: '#DBEAFE', text: '#2563EB', label: 'Ready' },
  LAUNCHED: { bg: '#D1FAE5', text: '#059669', label: 'Launched' },
  LIVE: { bg: '#D1FAE5', text: '#059669', label: 'Live' },
  PENDING_APPROVAL: { bg: '#FEF3C7', text: '#D97706', label: 'Pending Approval' },
  NEEDS_REVIEW: { bg: '#FEF3C7', text: '#D97706', label: 'Needs Review' },
};

const CreatorPortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [stats, setStats] = useState({});
  const [utilities, setUtilities] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [nftPreview, setNftPreview] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API}/creator/portfolios/${id}`, { headers }),
      axios.get(`${API}/creator/portfolios/${id}/utilities`, { headers }).catch(() => ({ data: { utilities: [] } })),
      axios.get(`${API}/creator/portfolios/${id}/readiness`, { headers }).catch(() => ({ data: { readiness: null } })),
      axios.get(`${API}/contracts/drafts`, { headers }).catch(() => ({ data: { drafts: [] } })),
    ]).then(async ([pRes, uRes, rRes, cRes]) => {
      const p = pRes.data.portfolio || {};
      setPortfolio(p);
      setStats(pRes.data.stats || {});
      setNftPreview(pRes.data.nft_preview || []);
      setUtilities(uRes.data.utilities || []);
      setReadiness(rRes.data?.readiness || null);
      // Canonical draft resolution — mirrors LaunchReadinessPage. The
      // portfolio.contract_id forward reference is the authoritative
      // relationship. We ALSO back-fill from the drafts list for
      // legacy records that only have the back-reference. Any draft
      // discovered by either path is deduped by id.
      const pid = p.id || id;
      const cid = p.contract_id;
      const found = new Map();
      for (const c of (cRes.data?.drafts || [])) {
        if (c.portfolio_id === pid || (cid && c.id === cid)) found.set(c.id, c);
      }
      if (cid && !found.has(cid)) {
        try {
          const dRes = await axios.get(`${API}/contracts/drafts/${cid}`, { headers });
          const draft = dRes.data?.draft || dRes.data;
          if (draft && draft.id) found.set(draft.id, draft);
        } catch (_) { /* 404 → fall through, card will show empty state */ }
      }
      setContracts(Array.from(found.values()));
    }).catch(() => toast.error('Failed to load portfolio'))
      .finally(() => setLoading(false));
  }, [id]);

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  const getGradient = () => {
    let h = 0; for (let i = 0; i < id.length; i++) h = ((h << 5) - h) + id.charCodeAt(i);
    return GRADIENTS[Math.abs(h) % GRADIENTS.length];
  };

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.muted }}>Loading...</div>;
  if (!portfolio) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.muted }}>Portfolio not found</div>;

  const badge = STATUS_BADGE[portfolio.status] || STATUS_BADGE.DRAFT;
  const isDraft = portfolio.status === 'DRAFT';
  const isActive = ['ACTIVE', 'LAUNCHED', 'LIVE'].includes(portfolio.status);

  // Batch 1C-A FINAL — canonical readiness comes from the backend so
  // Setup Progress and LaunchReadinessPage always agree on percentage,
  // blocker count and required denominator.
  const linkedDraft = readiness?.requirements?.find(r => r.id === 'contract');
  const hasContractDraft = !!(linkedDraft && linkedDraft.done);
  const initialSetupComplete = !!(readiness?.initial_setup_completed || portfolio.initial_setup_completed);
  const isLive = readiness?.is_live || ['LIVE','LAUNCHED','ACTIVE'].includes(portfolio.status);
  const savedStep = parseInt(portfolio.wizard_current_step, 10);

  const setupSteps = readiness ? readiness.requirements.map(r => {
    // Batch 1C-B1 Part 3 — the `deployment` requirement's action is
    // controlled by canonical deployment CTA state, NOT the raw
    // requirement cta_route. Never render an active Deploy button when
    // eligibility is disabled/hidden.
    let cta = null;
    if (!r.done && r.cta_route && !r.blocked) {
      cta = { label: r.cta_label, onClick: () => navigate(r.cta_route) };
    }
    if (r.id === 'deployment') {
      const dep = readiness.deployment || {};
      if (dep.deployment_cta_state === 'enabled') {
        cta = { label: 'Deploy Contract', onClick: () => navigate(`/creator/contracts/${portfolio.contract_id}/deploy`) };
      } else if (dep.deployment_cta_state === 'disabled') {
        const b = (dep.deployment_blockers || [])[0];
        if (b?.code === 'contract_code_missing' || b?.code === 'contract_config_incomplete') {
          cta = { label: 'Prepare Contract', onClick: () => navigate(b.resolution_route || `/creator/portfolios/${id}/edit?section=contract`) };
        } else if (b?.code === 'initial_setup_incomplete') {
          cta = { label: 'Resume Setup', onClick: () => navigate(b.resolution_route || `/creator/portfolios/new?edit=${id}&step=5`) };
        } else if (b?.code === 'already_deployed') {
          cta = { label: 'View Deployment', onClick: () => navigate(`/creator/contracts/${portfolio.contract_id}/deploy`) };
        } else {
          cta = null;
        }
      } else {
        cta = null; // hidden
      }
    }
    return {
      key: r.id, label: r.label, done: !!r.done, locked: !!r.blocked,
      requirement_level: r.requirement_level || (r.required ? 'required' : 'not_applicable'),
      cta,
    };
  }) : [];
  const requiredSteps = setupSteps.filter(s => s.requirement_level === 'required');
  const doneCount = readiness?.completed_required_count ?? 0;
  const totalRequired = readiness?.required_count ?? 0;
  const pct = readiness?.percent ?? 0;

  return (
    <div data-testid="portfolio-detail-page" style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>
      <button data-testid="portfolio-detail-back" onClick={() => navigate('/creator/portfolios')}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: '14px', fontWeight: 600, marginBottom: '20px' }}>
        <ArrowLeft size={16} /> All Portfolios
      </button>

      {/* Bug #35 — Setup Progress: what's done, what still needs setup */}
      <Card data-testid="setup-progress-card" style={{ marginBottom: '20px', border: `1px solid ${theme.colors.borders}` }}>
        <CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Setup Progress</h3>
              <p data-testid="setup-progress-pct" style={{ fontSize: '12px', color: theme.colors.text.muted, marginTop: 2 }}>
                Kickback Setup — {pct}% complete ({doneCount} of {totalRequired} required)
              </p>
            </div>
            <span data-testid="setup-progress-status" style={{
              padding: '4px 12px', borderRadius: 999, background: badge.bg, color: badge.text,
              fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>Status: {badge.label}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {setupSteps.map((s) => (
              <div
                key={s.key}
                data-testid={`setup-step-${s.key}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, flexWrap: 'wrap',
                  padding: '10px 12px',
                  background: s.done ? `${theme.colors.status.success}10` : theme.colors.bg.secondary,
                  border: `1px solid ${s.done ? theme.colors.status.success + '55' : theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {s.done
                    ? <span style={{ color: theme.colors.status.success, display: 'inline-flex' }}>●</span>
                    : <span style={{ color: theme.colors.text.muted, display: 'inline-flex' }}>○</span>}
                  <span style={{
                    fontSize: 13, fontWeight: 600,
                    color: s.done ? theme.colors.status.success : theme.colors.text.primary,
                    textDecoration: s.done ? 'none' : 'none',
                  }}>{s.label}</span>
                  {s.requirement_level && s.requirement_level !== 'required' && (
                    <span data-testid={`setup-step-${s.key}-level`} style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                      padding: '2px 6px', borderRadius: 999,
                      background: s.requirement_level === 'recommended' ? '#FEF3C7' : '#E5E7EB',
                      color: s.requirement_level === 'recommended' ? '#B45309' : '#4B5563',
                    }}>
                      {s.requirement_level === 'recommended' ? 'Recommended' : 'N/A'}
                    </span>
                  )}
                  {!s.done && s.requirement_level === 'required' && (
                    <span style={{
                      fontSize: 11, color: theme.colors.text.muted, fontWeight: 500,
                    }}>needs setup</span>
                  )}
                </div>
                {!s.done && s.cta && (
                  <button
                    data-testid={`setup-step-${s.key}-cta`}
                    onClick={s.cta.onClick}
                    style={{
                      padding: '6px 12px', border: 'none', cursor: 'pointer',
                      borderRadius: theme.radius.sm, background: theme.colors.primary,
                      color: '#FFF', fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {s.cta.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 1. Hero Section */}
      <div style={{ borderRadius: theme.radius.card, overflow: 'hidden', marginBottom: '28px', border: `1px solid ${theme.colors.borders}` }}>
        <div style={{
          height: '160px', background: portfolio.cover_image_url ? `url(${portfolio.cover_image_url}) center/cover` : getGradient(),
          position: 'relative',
        }}>
          <span style={{ position: 'absolute', top: '14px', right: '14px', padding: '4px 14px', borderRadius: '16px', fontSize: '12px', fontWeight: 600, background: badge.bg, color: badge.text }}>{badge.label}</span>
        </div>
        <div style={{ padding: '24px', background: theme.colors.bg.card }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 data-testid="portfolio-detail-name" style={{ fontSize: '26px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '4px', fontFamily: theme.fonts.heading }}>{portfolio.name}</h1>
              {portfolio.description && <p style={{ fontSize: '14px', color: theme.colors.text.secondary, maxWidth: '500px' }}>{portfolio.description}</p>}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {isDraft && !initialSetupComplete && (
                <Button data-testid="portfolio-resume-btn" onClick={() => navigate(`/creator/portfolios/new?edit=${id}`)} style={{ gap: '6px', background: theme.colors.primary, color: '#FFF' }}>
                  <Edit3 size={14} /> Resume Setup
                </Button>
              )}
              {initialSetupComplete && (
                <Button data-testid="portfolio-edit-btn" onClick={() => navigate(`/creator/portfolios/${id}/edit`)} style={{ gap: '6px', background: theme.colors.primary, color: '#FFF' }}>
                  <Edit3 size={14} /> Edit Kickback
                </Button>
              )}
              <Button data-testid="portfolio-launch-readiness-btn" onClick={() => navigate(`/creator/portfolios/${id}/launch-readiness`)} variant="outline" style={{ gap: '6px' }}>
                <Rocket size={14} /> Review Launch Readiness
              </Button>
              {(() => {
                // Canonical hero deployment control.
                //   • enabled   → "Deploy Contract" (active link)
                //   • already deployed → "View Deployment" (active link to deploy workspace / explorer)
                //   • other disabled states → shown but disabled with tooltip explaining the blocker
                //   • hidden   → nothing
                const dep = readiness?.deployment;
                if (!dep || dep.deployment_cta_state === 'hidden') return null;
                const alreadyDeployed = (dep.deployment_blockers || []).some(b => b.code === 'already_deployed');
                if (alreadyDeployed) {
                  return (
                    <Button data-testid="portfolio-view-deployment-btn"
                      onClick={() => navigate(`/creator/contracts/${portfolio.contract_id}/deploy`)}
                      title="Open the deployed contract workspace"
                      style={{ gap: '6px', background: theme.colors.status.success, color: '#FFF' }}>
                      <Rocket size={14} /> View Deployment
                    </Button>
                  );
                }
                const disabled = dep.deployment_cta_state !== 'enabled';
                const blockerLabel = dep.deployment_blockers?.[0]?.label;
                return (
                  <Button data-testid="portfolio-deploy-btn"
                    onClick={() => { if (!disabled) navigate(`/creator/contracts/${portfolio.contract_id}/deploy`); }}
                    disabled={disabled}
                    title={disabled ? (blockerLabel || 'Not eligible for deployment yet') : 'Deploy this Kickback\u2019s contract draft'}
                    style={{ gap: '6px', background: theme.colors.primary, color: '#FFF', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                    <Rocket size={14} /> Deploy Contract
                  </Button>
                );
              })()}
            </div>
          </div>

          {/* Key Stats Row */}
          <div style={{ display: 'flex', gap: '32px', marginTop: '20px', flexWrap: 'wrap' }}>
            {[
              { icon: <Package size={14} />, label: 'Type', value: portfolio.portfolio_type || 'ERC721' },
              { icon: <DollarSign size={14} />, label: 'Unit Price', value: portfolio.unit_price ? `${portfolio.unit_price} ETH` : '-' },
              { icon: <Users size={14} />, label: 'Quantity', value: portfolio.quantity || '-' },
              { icon: <BarChart2 size={14} />, label: 'Kickback', value: portfolio.kickback_percent ? `${portfolio.kickback_percent}%` : '-' },
              { icon: <Layers size={14} />, label: 'Sales', value: stats.total_sales || 0 },
              { icon: <DollarSign size={14} />, label: 'Revenue', value: stats.total_revenue ? `$${stats.total_revenue.toFixed(2)}` : '$0.00' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: theme.colors.primary }}>{s.icon}</span>
                <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{s.label}:</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. Contracts Section */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileCode size={16} color={theme.colors.primary} />
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Smart Contracts</h3>
          </div>
          <Button data-testid="manage-contracts-btn" variant="outline" size="sm" onClick={() => navigate('/creator/contracts')} style={{ fontSize: '12px' }}>
            Manage Contracts
          </Button>
        </div>
        {contracts.length === 0 ? (
          <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>No contract drafts yet. Create one to deploy your collection on-chain.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {contracts.map(c => (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
                borderRadius: '8px', background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.borders}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: `${theme.colors.primary}14`, color: theme.colors.primary }}>{c.standard}</span>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: theme.colors.text.primary }}>{c.contract_name || c.template_name || 'Draft'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {c.deployed ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', color: theme.colors.status.success, fontWeight: 600 }}>Deployed</span>
                      <code style={{ fontSize: '11px', color: theme.colors.text.muted, fontFamily: 'monospace' }}>{c.deployed_address?.slice(0, 8)}...{c.deployed_address?.slice(-6)}</code>
                      <button onClick={() => copyText(c.deployed_address)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                        <Copy size={12} color={theme.colors.text.muted} />
                      </button>
                      <a href={`https://amoy.polygonscan.com/address/${c.deployed_address}`} target="_blank" rel="noreferrer">
                        <ExternalLink size={12} color={theme.colors.primary} />
                      </a>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => navigate(`/creator/contracts/${c.id}/deploy`)}
                      style={{ fontSize: '11px', background: `${theme.colors.status.info}18`, color: theme.colors.status.info, border: 'none' }}>
                      <Rocket size={12} /> Deploy
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* 3. Utilities Attached */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Link2 size={16} color={theme.colors.status.success} />
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Utilities Attached ({utilities.length})</h3>
        </div>
        {utilities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <p style={{ fontSize: '13px', color: theme.colors.text.muted, marginBottom: '12px' }}>No utilities attached. Browse the marketplace to add value to your collection.</p>
            <Button variant="outline" size="sm" onClick={() => navigate('/creator/utilities-marketplace')}>Browse Marketplace</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {utilities.map((u, i) => (
              <div key={u.link_id || i} onClick={() => navigate(`/creator/utilities/${u.listing_id}`)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
                  borderRadius: '8px', background: theme.colors.bg.secondary, cursor: 'pointer',
                  border: `1px solid ${theme.colors.borders}`, transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = theme.colors.shadows.sm}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{u.title}</p>
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{u.provider_name} · {u.fulfillment_type}</p>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.primary }}>
                  {u.pricing_model === 'FREE' ? 'Free' : `$${u.price_amount}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

              {/* 4. Kickback Artwork (renamed from "NFT Art Preview" per B1C-A
          §10). Shows the artwork the Creator selected during the Create
          Kickback wizard; distinct from Item / Offer Evidence below. */}
      <Card data-testid="kickback-artwork-card" style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Image size={16} color={theme.colors.primary} />
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Kickback Artwork</h3>
          <span data-testid="kickback-artwork-count" style={{ fontSize: '12px', color: theme.colors.text.muted }}>
            {(portfolio.nft_asset_ids || []).length} selected
          </span>
        </div>
        {nftPreview.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px', background: theme.colors.bg.secondary, borderRadius: theme.radius.md, border: `1px dashed ${theme.colors.borders}` }}>
            <Image size={32} color={theme.colors.text.muted} style={{ marginBottom: '12px' }} />
            <p style={{ fontSize: '14px', color: theme.colors.text.muted, marginBottom: '12px' }}>No Kickback artwork selected yet.</p>
            <Button variant="outline" size="sm" onClick={() => navigate(`/creator/portfolios/new?edit=${id}`)}>
              Edit Kickback Setup
            </Button>
          </div>
        ) : (
          <div data-testid="nft-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
            {nftPreview.map(n => (
              <div key={n.id} data-testid={`nft-thumb-${n.id}`} style={{
                aspectRatio: '1', borderRadius: theme.radius.sm, overflow: 'hidden',
                border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.secondary,
              }}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${n.url}`} alt="Kickback artwork" loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* Batch 1C-A §11 — Item / Offer Evidence (Step 1 material).
          Explicitly separate from Kickback Artwork so private evidence
          never mixes with public artwork. Bytes are still served through
          the owner-gated /api/nft/file/{id} endpoint. */}
      {(() => {
        // Canonical evidence counting — mirrors `_compute_readiness`
        // in /app/backend/api/routers/portfolios.py so Portfolio Detail,
        // Launch Readiness and Edit Evidence always report the same
        // numbers. Flat keys (legacy) + structured `uploads.*` (current).
        const ev = portfolio.offer_evidence || {};
        const countFlat = (keys) => keys.reduce((n, k) => n + (Array.isArray(ev[k]) ? ev[k].length : 0), 0);
        let evImagesCount = countFlat(['evidence_images', 'images', 'product_images', 'proof_images', 'design_images']);
        let evDocsCount = countFlat(['supporting_documents', 'documents', 'screenshots']);
        const uploads = (ev.uploads && typeof ev.uploads === 'object') ? ev.uploads : {};
        for (const grp of Object.keys(uploads)) {
          const files = uploads[grp];
          if (!Array.isArray(files)) continue;
          if (grp === 'supporting_documents') evDocsCount += files.length;
          else evImagesCount += files.length;
        }
        const evOfferType = ev.offer_type || ev.type || null;
        return (
          <Card data-testid="offer-evidence-card" style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={16} color={theme.colors.status.info} />
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>
                  Item / Offer Evidence
                </h3>
              </div>
              <Button
                data-testid="offer-evidence-edit-btn"
                variant="outline" size="sm"
                onClick={() => navigate(`/creator/portfolios/new?edit=${id}&step=1&section=evidence`)}
                style={{ fontSize: '12px' }}
              >
                <Edit3 size={12} /> Edit Evidence
              </Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 24px' }}>
              <div>
                <p style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Offer Type</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>{evOfferType || 'Not set'}</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Evidence Images</p>
                <p data-testid="evidence-image-count" style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>{evImagesCount}</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Supporting Documents</p>
                <p data-testid="evidence-document-count" style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text.primary }}>{evDocsCount}</p>
              </div>
            </div>
          </CardContent></Card>
        );
      })()}

      {/* P10.6: Product Details */}
      {(portfolio.product_name || portfolio.sku || portfolio.manufacturer || portfolio.origin_country || portfolio.product_image_url || (Array.isArray(portfolio.patent_numbers) ? portfolio.patent_numbers.length > 0 : !!portfolio.patent_numbers)) && (
        <Card data-testid="product-details-card" style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Package size={16} color={theme.colors.status.info} />
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Product Details</h3>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {portfolio.product_image_url && (
              <img src={portfolio.product_image_url} alt={portfolio.product_name || 'Product'}
                style={{ width: '100px', height: '100px', borderRadius: theme.radius.sm, objectFit: 'cover', border: `1px solid ${theme.colors.borders}` }}
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 20px' }}>
              {[
                ['Product Name', portfolio.product_name],
                ['SKU', portfolio.sku],
                ['Manufacturer', portfolio.manufacturer],
                ['Origin Country', portfolio.origin_country],
                ['Production Date', portfolio.production_date],
                ['Patent Numbers', Array.isArray(portfolio.patent_numbers) ? portfolio.patent_numbers.join(', ') : (portfolio.patent_numbers || '')],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <p style={{ fontSize: '11px', color: theme.colors.text.muted, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</p>
                  <p data-testid={`product-${label.toLowerCase().replace(/\s+/g, '-')}`} style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary, wordBreak: 'break-word' }}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent></Card>
      )}

      {/* 5. Timeline */}
      <Card><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Clock size={16} color={theme.colors.text.muted} />
          <h3 style={{ fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary }}>Timeline</h3>
        </div>
        <div style={{ borderLeft: `2px solid ${theme.colors.borders}`, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.colors.primary, marginLeft: '-24px' }} />
              <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Portfolio Created</span>
            </div>
            <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginLeft: '0' }}>{new Date(portfolio.created_at).toLocaleString()}</p>
          </div>

          {utilities.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.colors.status.success, marginLeft: '-24px' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>{utilities.length} Utilities Attached</span>
              </div>
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Earliest: {new Date(utilities[utilities.length - 1]?.attached_at || portfolio.created_at).toLocaleString()}</p>
            </div>
          )}

          {contracts.filter(c => c.deployed).length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.colors.status.info, marginLeft: '-24px' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Contract Deployed</span>
              </div>
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{contracts.filter(c => c.deployed)[0]?.deployed_chain} · {contracts.filter(c => c.deployed)[0]?.deployed_address?.slice(0, 16)}...</p>
            </div>
          )}

          {isActive && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: theme.colors.status.success, marginLeft: '-24px' }} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Portfolio Active</span>
              </div>
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Accepting buyers</p>
            </div>
          )}
        </div>
      </CardContent></Card>

      {/* Batch 1C-B1 Part 2 — legacy LaunchReadinessPanel removed. The
          Setup Progress card above already renders the canonical readiness
          summary; users click "Review Launch Readiness" for the full
          control center. No duplicate readiness math on this page. */}

      {/* RAP Kickback Gifts panel */}
      <RapPanel portfolioId={id} />

      {/* Phase 3 — Claim Links & QR Codes panel */}
      <ClaimLinksPanel portfolioId={id} />
      <UtilityRulesPanel portfolioId={id} />

      {/* Phase 8 — Attached Utilities (from Utility Vault) */}
    </div>
  );
};

export default CreatorPortfolioDetail;
